'use client';
import { Spinner } from '@shared/icons';
import { forwardRef, useCallback, useRef } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '../../../shadcn';
import { TextInput } from '../../TextInput/TextInput';
import { useAddressLookupController } from '../hooks/UseAddressLookupController';
import type { AddressLookupProps, ElementWithResizeObserver } from '../types';
import { SuggestionsList } from './SuggestionsList';

// Extract ResizeObserver callback for testability
const createResizeObserverCallback = (onContentHeightChange?: (height: number) => void) => {
  return (entries: ResizeObserverEntry[]) => {
    const rect = entries[0]?.contentRect;
    if (!rect) return;
    onContentHeightChange?.(rect.height || 0);
  };
};

// Extract event handlers for testability
const handleWheelCapture = (e: React.WheelEvent) => e.stopPropagation();
const handleTouchMoveCapture = (e: React.TouchEvent) => e.stopPropagation();
const handleOpenAutoFocus = (e: Event) => e.preventDefault();
const handleInteractOutside = (e: Event) => e.preventDefault();

// Extract setPopoverNode logic for testability
const createSetPopoverNodeCallback = (popoverRef: React.MutableRefObject<HTMLDivElement | null>, onContentHeightChange?: (height: number) => void) => {
  return (node: HTMLDivElement | null) => {
    // Disconnect previous observer if any
    const previousElement = popoverRef.current as ElementWithResizeObserver | null;
    if (previousElement?.storedResizeObserver) {
      previousElement.storedResizeObserver.disconnect();
    }

    popoverRef.current = node;

    if (!node) return;

    // Initial height
    onContentHeightChange?.(node.clientHeight || 0);

    // Observe future height changes
    const resizeObserver = new ResizeObserver(createResizeObserverCallback(onContentHeightChange));
    resizeObserver.observe(node);
    // Store observer for cleanup on next ref change
    (node as ElementWithResizeObserver).storedResizeObserver = resizeObserver;
  };
};

/**
 * AddressLookup component provides an address autocomplete functionality
 * with suggestions as the user types.
 */
const AddressLookup = forwardRef<HTMLInputElement, AddressLookupProps>(function AddressLookup(
  {
    id,
    name,
    label = 'Address',
    placeholder = 'Start typing your address...',
    className = '',
    value = '',
    onChange,
    onAddressSelect,
    onContentHeightChange,
    topActionElement,
    helpMessage,
    helpMessageType,
    disabled = false,
    onOpenChange,
    inputTrackingProps,
    helperTrackingProps,
  },
  ref
) {
  // Use a single controller hook to manage all the component's logic
  const {
    inputRef,
    suggestions,
    isLoading,
    error,
    open,
    highlightedIndex,
    isScrollable,
    suggestionsRef,
    handleInputChange,
    handleInputKeyDown,
    handleItemKeyDown,
    handleSelect,
    handleItemMouseEnter,
    handleFocus,
    handlePopoverOpenChange,
    ...props
  } = useAddressLookupController({
    ref,
    value,
    onChange,
    onAddressSelect,
    onOpenChange,
  });
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Callback ref that manages a ResizeObserver on the popover content
  // Reports height changes to the parent so the spacer can match the popover's current height
  const setPopoverNode = useCallback((node: HTMLDivElement | null) => createSetPopoverNodeCallback(popoverRef, onContentHeightChange)(node), [onContentHeightChange]);

  // Determine if we should render the content
  const shouldRenderContent = suggestions.length > 0 && open;

  return (
    <div className={`w-full relative ${className}`} data-testid="address-lookup-wrapper">
      <Popover open={open} onOpenChange={handlePopoverOpenChange}>
        <PopoverAnchor asChild>
          <TextInput
            {...props}
            id={id}
            name={name}
            ref={ref || inputRef}
            label={label}
            value={value || ''}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            rightIcon={isLoading ? <Spinner className="h-5 w-5 text-primary animate-spin" /> : undefined}
            helpMessage={error || helpMessage}
            helpMessageType={error ? 'error' : helpMessageType}
            topActionElement={topActionElement}
            disabled={disabled}
            onKeyDown={handleInputKeyDown}
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={open ? `${id}-suggestions` : undefined}
            aria-activedescendant={highlightedIndex >= 0 ? `${id}-suggestion-${highlightedIndex}` : undefined}
            aria-invalid={helpMessageType === 'error' || error ? 'true' : undefined}
            data-testid="address-lookup-input"
            inputTrackingProps={inputTrackingProps}
            helperTrackingProps={helperTrackingProps}
          />
        </PopoverAnchor>
        {shouldRenderContent && (
          <PopoverContent
            ref={setPopoverNode}
            className="p-0 w-[var(--radix-popover-trigger-width)] border-none shadow-none rounded-none z-[9999] relative pointer-events-auto"
            style={{
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              transform: 'none',
              transition: 'none',
              animation: 'none',
              overscrollBehavior: 'contain',
            }}
            align="start"
            id={`${id}-suggestions`}
            onOpenAutoFocus={handleOpenAutoFocus}
            onInteractOutside={handleInteractOutside}
            sideOffset={0}
            data-testid="suggestions-popover"
            data-is-scrollable={isScrollable ? 'true' : 'false'}
            avoidCollisions={false}
            forceMount
          >
            <div className="py-0">
              <div
                className="max-h-[360px] overflow-y-auto overscroll-contain"
                ref={suggestionsRef}
                style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                onWheelCapture={handleWheelCapture}
                onTouchMoveCapture={handleTouchMoveCapture}
              >
                <SuggestionsList
                  id={id}
                  suggestions={suggestions}
                  highlightedIndex={highlightedIndex}
                  onSelect={handleSelect}
                  onMouseEnter={handleItemMouseEnter}
                  onItemKeyDown={handleItemKeyDown}
                  data-track-container="Addresses"
                  data-track-privacy="Mask"
                />
              </div>
            </div>
            {/* Gradient overlay - only shown when content is scrollable */}
            {isScrollable && (
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-background to-transparent pointer-events-none z-10" data-testid="gradient-overlay" />
            )}
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
});

// Export the component and testable handlers at the end of the file
export { AddressLookup, createResizeObserverCallback, createSetPopoverNodeCallback, handleInteractOutside, handleOpenAutoFocus, handleTouchMoveCapture, handleWheelCapture };
